/**
 * InviteUserDialog — modal para invitar un usuario al tenant.
 *
 * Pide: email + tier de licencia (editor / reviewer / external / admin).
 *
 * Cuando el tier es `admin` aparece un checkbox de confirmación
 * obligatoria — el backend rechaza el invite con HTTP 400 si
 * `confirm_admin` no es `true` (control sensible: admin tiene
 * acceso total al tenant).
 *
 * `auditor` queda fuera del selector hasta que se implemente el rol
 * read-only cross-project (Doc 12 §Roles). El backend lo soporta.
 *
 * `is_external` no es un input — el backend lo deriva del rol:
 *   - role=client     → is_external=true
 *   - role=member     → is_external=false
 *   - role=admin → is_external=false
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../api/client'
import { createInvite } from '../../api/invites'
import type { UserTenantRole } from '../../api/me'
import { useAccessToken } from '../../auth/useAccessToken'
import { tierHelpKey, tierLabelKey } from '../../lib/tiers'
import { Dialog } from '../Dialog'
import { pushToast } from '../../stores/toastStore'

interface InviteUserDialogProps {
  open: boolean
  onClose: () => void
  tenantId: string
}

type InvitableRole = UserTenantRole

// Los cuatro tiers son invitables: el tier decide el asiento, y el
// alta por invitación es donde se elige (OT-0008 §4.2).
const INVITABLE_ROLES: InvitableRole[] = ['editor', 'reviewer', 'external', 'admin']

export function InviteUserDialog({
  open,
  onClose,
  tenantId,
}: InviteUserDialogProps) {
  const { t } = useTranslation(['team', 'errors', 'common'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<InvitableRole>('editor')
  const [confirmAdmin, setConfirmAdmin] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      const trimmed = email.trim()
      if (!trimmed) {
        throw new Error('validation')
      }
      return createInvite(accessToken, tenantId, {
        email: trimmed,
        role,
        ...(role === 'admin' ? { confirm_admin: true } : {}),
      })
    },
    onSuccess: (invite) => {
      pushToast({
        kind: 'success',
        message: t('invite_modal.success', { email: invite.email }),
      })
      void queryClient.invalidateQueries({
        queryKey: ['tenant-invites', tenantId],
      })
      reset()
      onClose()
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        // El backend devuelve 'detail' como string en HTTPException — no
        // pasa por el catálogo errors.{code}. Mostramos detail si vino,
        // sino una copy genérica.
        const detail =
          (err.body as unknown as { detail?: string } | null)?.detail ||
          err.message
        setError(detail || t('invite_modal.error_generic'))
      } else if ((err as Error).message !== 'validation') {
        setError(t('invite_modal.error_generic'))
      }
    },
  })

  const reset = () => {
    setEmail('')
    setRole('editor')
    setConfirmAdmin(false)
    setError(null)
  }

  const handleClose = () => {
    if (mutation.isPending) return
    reset()
    onClose()
  }

  const submitDisabled =
    mutation.isPending ||
    !email.trim() ||
    (role === 'admin' && !confirmAdmin)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('invite_modal.title')}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={mutation.isPending}
            className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm text-ink hover:bg-surface-muted disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null)
              mutation.mutate()
            }}
            disabled={submitDisabled}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-canvas hover:bg-brand/90 disabled:opacity-50"
          >
            {mutation.isPending
              ? t('invite_modal.submitting')
              : t('invite_modal.submit')}
          </button>
        </>
      }
    >
      <p className="font-body text-sm text-ink-muted mb-4">
        {t('invite_modal.subtitle')}
      </p>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="invite-email"
            className="block text-xs font-medium text-ink mb-1"
          >
            {t('invite_modal.email_label')}
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('invite_modal.email_placeholder')}
            className="w-full rounded-md border border-line bg-surface px-3 py-2 font-mono text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand"
            autoFocus
            disabled={mutation.isPending}
          />
          <p className="mt-1 text-[11px] text-ink-subtle">
            {t('invite_modal.email_help')}
          </p>
        </div>

        <fieldset>
          <legend className="block text-xs font-medium text-ink mb-1">
            {t('invite_modal.role_label')}
          </legend>
          <div className="space-y-1.5">
            {INVITABLE_ROLES.map((value) => {
              const helpKey = tierHelpKey(value)
              return (
                <label
                  key={value}
                  className={`flex gap-2 items-start rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                    role === value
                      ? 'border-brand bg-brand-soft'
                      : 'border-line bg-surface hover:bg-surface-muted'
                  }`}
                >
                  <input
                    type="radio"
                    name="invite-role"
                    value={value}
                    checked={role === value}
                    onChange={() => {
                      setRole(value)
                      if (value !== 'admin') setConfirmAdmin(false)
                    }}
                    disabled={mutation.isPending}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium text-ink">
                      {t(tierLabelKey(value))}
                    </span>
                    <span className="block text-[11px] text-ink-muted mt-0.5">
                      {t(helpKey)}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
        </fieldset>

        {role === 'admin' && (
          <label className="flex gap-2 items-start rounded-md border border-review/30 bg-review-soft px-3 py-2">
            <input
              type="checkbox"
              checked={confirmAdmin}
              onChange={(e) => setConfirmAdmin(e.target.checked)}
              disabled={mutation.isPending}
              className="mt-0.5"
            />
            <span className="text-xs text-ink">
              {t('invite_modal.confirm_admin_label')}
            </span>
          </label>
        )}

        {error && (
          <p
            role="alert"
            className="text-xs text-critical bg-critical-soft border border-critical/30 rounded-md px-3 py-2"
          >
            {error}
          </p>
        )}
      </div>
    </Dialog>
  )
}
